/**
 * Natural order for identifiers and names: SUB_GNC_2 before SUB_GNC_10.
 *
 * Requirement, case and part ids end in numbers, and plain string order puts 10
 * before 2 (issue #82). Padding the numbers with zeros would fix the order and
 * rewrite every id, so the order is taught instead: a run of digits compares as
 * a NUMBER and everything else as text. Ids themselves never change; this only
 * decides where a row sits.
 *
 * The same rule is in the back end (`best/metamodel/ordering.py`), which sorts
 * what the server sorts - the Flowdown tree, the CSV exports. This one sorts
 * what the browser sorts: a grid's column, a picker's options.
 *
 * - CASE is ignored: `sub_2` and `SUB_2` compare equal. (`sensitivity: 'base'`
 *   also ignores accents, which ids do not carry.)
 * - EMPTY or missing sorts first, and never throws.
 * - MIXED text keeps its parts apart: `TC_2a` comes after `TC_2` and before
 *   `TC_10`.
 * - PADDING is not part of the number: `MS_02` and `MS_2` compare equal, and the
 *   (stable) sort keeps them in the order they arrived.
 * - A LIST (a cell holding several ids) compares as the ids joined by ', '.
 *
 * Pinned to 'en', as `quickFilter.ts` pins its `localeCompare`: a list that
 * sorts differently on two machines makes a link read differently to two
 * people.
 *
 * Not for orders that MEAN something - a plan's steps, state transitions, the
 * levels L0 to L4, statuses - and not for people's names, which keep theirs.
 */

import type { FieldKind } from '../../schema'

const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' })

/** What a cell or option is compared as: text, with a list joined and a blank empty. */
function asText(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (Array.isArray(value)) return value.map(asText).join(', ')
  return String(value)
}

/** Compare two ids or names in natural order. Usable directly as a `sort` comparator. */
export function naturalCompare(a: unknown, b: unknown): number {
  return collator.compare(asText(a), asText(b))
}

/**
 * The grid's `sortComparator` for a column of ids or names.
 *
 * Takes the cell VALUES the grid hands a comparator - after the column's
 * `valueGetter`, so a list column that joins its ids is compared as the joined
 * text. Both directions come from the grid, which reverses this.
 */
export const naturalComparator = (a: unknown, b: unknown): number => naturalCompare(a, b)

/** A copy of `items` in natural order, by the text `key` names (the item itself by default). */
export function sortNaturally<T>(items: readonly T[], key?: (item: T) => unknown): T[] {
  return [...items].sort((a, b) => (key ? naturalCompare(key(a), key(b)) : naturalCompare(a, b)))
}

/**
 * The field kinds that hold an id, a name or a list of ids, as opposed to prose
 * (`doc`), a choice (`enum`), a number (`quantity`) or a flag.
 *
 * Those four keep the comparator the grid gives them. A kind added to the
 * registry is NOT here until somebody decides what it is, which sorts it the
 * way it always did rather than guessing.
 */
const IDENTIFYING_KINDS: ReadonlySet<FieldKind> = new Set<FieldKind>([
  'text',
  'name',
  'identifier',
  'reference',
  'text_list',
  'reference_list',
  'type',
])

/** Whether a field of this kind should sort naturally. */
export function sortsNaturally(kind: FieldKind): boolean {
  return IDENTIFYING_KINDS.has(kind)
}
